import React from 'react';
import {
  ensureStoredProfileIcon,
  PROFILE_STATE_UPDATED_EVENT,
  getProfileFullName,
  parseProfileIconPayload
} from './profileIconUtils';

export default function ProfileIcon({ size = 38 }) {
  const [iconPayload, setIconPayload] = React.useState(() => {
    if (typeof window === 'undefined') return null;
    return ensureStoredProfileIcon();
  });
  const [imageLoadFailed, setImageLoadFailed] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const syncProfileIcon = () => {
      setImageLoadFailed(false);
      setIconPayload(ensureStoredProfileIcon());
    };

    syncProfileIcon();
    window.addEventListener('storage', syncProfileIcon);
    window.addEventListener(PROFILE_STATE_UPDATED_EVENT, syncProfileIcon);
    return () => {
      window.removeEventListener('storage', syncProfileIcon);
      window.removeEventListener(PROFILE_STATE_UPDATED_EVENT, syncProfileIcon);
    };
  }, []);

  React.useEffect(() => {
    setImageLoadFailed(false);
  }, [iconPayload?.src]);

  if (!iconPayload) return null;

  const borderColor = iconPayload.background === '#ffffff' ? 'rgba(0,0,0,0.18)' : 'transparent';
  const tooltipLabel = iconPayload.fullName
    || getProfileFullName({
      firstName: localStorage.getItem('appstar_first_name') || '',
      lastName: localStorage.getItem('appstar_last_name') || ''
    })
    || iconPayload.initials
    || 'User';

  if (iconPayload.kind === 'image' && iconPayload.src && !imageLoadFailed) {
    return (
      <img
        src={iconPayload.src}
        alt=""
        title={tooltipLabel}
        onError={() => {
          setImageLoadFailed(true);
          const fallbackPayload = parseProfileIconPayload(iconPayload);
          if (fallbackPayload) {
            setIconPayload({
              ...fallbackPayload,
              kind: 'initials',
              src: ''
            });
          }
        }}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          objectFit: 'cover',
          display: 'block',
          flexShrink: 0,
          border: `1px solid ${borderColor}`
        }}
      />
    );
  }

  return (
    <div
      aria-label="User profile icon"
      title={tooltipLabel}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: iconPayload.background || '#ffffff',
        color: iconPayload.textColor || '#000000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: Math.max(14, Math.round(size * 0.38)),
        fontWeight: 700,
        lineHeight: 1,
        textTransform: 'uppercase',
        flexShrink: 0,
        border: `1px solid ${borderColor}`,
        boxSizing: 'border-box'
      }}
    >
      {String(iconPayload.initials || '').slice(0, 2).toUpperCase()}
    </div>
  );
}